import { useMutation } from "@apollo/client/react";
import {
  AdminMultipleChoiceQuestionOptionFragment,
  DeleteMultipleChoiceQuestion,
  DeleteMultipleChoiceQuestionVariables,
  MultipleChoiceQuestionFragment,
  MultipleChoiceType,
  ReorderMultipleChoiceQuestionOption,
  ReorderMultipleChoiceQuestionOptionVariables,
  UpdateMultipleChoiceQuestionDetails,
  UpdateMultipleChoiceQuestionDetailsVariables,
} from "__generated__/types";
import classNames from "classnames";
import barsicon from "common/assets/font-awesome/bars-solid.svg";
import deleteIcon from "common/assets/font-awesome/delete.svg";
import { IconButton, TextButton } from "common/buttons";
import { useConfirmDialog } from "common/components/confirm-dialog/use-confirm-dialog";
import { FieldError } from "common/components/forms/FieldError";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { InputField } from "common/components/InputField";
import { ListContainer } from "common/components/lists/ListContainer";
import { ListItem } from "common/components/lists/ListItem";
import { useDebounce } from "common/hooks/useDebounce";
import {
  DnDItemType,
  useDragAndDropElement,
} from "common/hooks/useDragAndDropElement";
import { useToggle } from "common/hooks/useToggle";
import {
  extractNullableMarkdownContent,
  markdownInput,
} from "common/input-types";
import { preventDragOnTextInput } from "common/utils/element-utils";
import { CreateQuestionOptionForm } from "domain/admin/multiple-choice/CreateQuestionOptionForm";
import gql from "graphql-tag";
import update from "immutability-helper";
import * as React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import questionStyles from "./QuestionListItem.module.css";
import { QuestionListOptionItem } from "./QuestionListOptionItem";

const REORDER_OPTIONS_MUTATION = gql`
  mutation ReorderMultipleChoiceQuestionOption(
    $input: ReorderMultipleChoiceQuestionOptionInput!
  ) {
    reorderMultipleChoiceQuestionOption(input: $input) {
      option {
        id
      }
    }
  }
`;

const UPDATE_DETAILS_MUTATION = gql`
  mutation UpdateMultipleChoiceQuestionDetails(
    $input: UpdateMultipleChoiceQuestionDetailsInput!
  ) {
    updateMultipleChoiceQuestionDetails(input: $input) {
      multipleChoiceQuestion {
        id
        title
      }
    }
  }
`;

const DELETE_QUESTION = gql`
  mutation DeleteMultipleChoiceQuestion(
    $input: DeleteMultipleChoiceQuestionInput!
  ) {
    deleteMultipleChoiceQuestion(input: $input) {
      multipleChoice {
        id
        title
      }
    }
  }
`;

interface Props {
  question: MultipleChoiceQuestionFragment;
  multipleChoiceId: string;
  type: MultipleChoiceType;
  index: number;
  moveElement(dragIndex: number, hoverIndex: number): void;
  saveOrder(dragIndex: number, dropIndex: number, id: string): void;
  refetch(): void;
}

interface UpdateMultipleChoiceQuestionForm {
  multipleChoiceId: string;
  multipleChoiceQuestionId: string;
  title: string;
  body: string;
}

export const QuestionListItem: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  multipleChoiceId,
  type,
  index,
  moveElement,
  saveOrder,
  refetch,
}) => {
  const [isExpanded, setIsExpanded] = useToggle(false);
  const [questionOptionElements, setQuestionOptionElements] = React.useState<
    AdminMultipleChoiceQuestionOptionFragment[]
  >(question.options || []);

  React.useEffect(() => {
    setQuestionOptionElements(question.options || []);
  }, [question]);

  const methods = useForm<UpdateMultipleChoiceQuestionForm>({
    defaultValues: {
      title: question.title,
      body: extractNullableMarkdownContent(question.body) ?? "",
    },
  });
  const { register, handleSubmit, watch } = methods;

  const [
    updateDetailsMutation,
    { loading: isUpdatingDetails, error: updateDetailsError },
  ] = useMutation<
    UpdateMultipleChoiceQuestionDetails,
    UpdateMultipleChoiceQuestionDetailsVariables
  >(UPDATE_DETAILS_MUTATION);

  const [
    deleteQuestionMutation,
    { loading: isDeletingQuestion, error: deleteQuestionError },
  ] = useMutation<
    DeleteMultipleChoiceQuestion,
    DeleteMultipleChoiceQuestionVariables
  >(DELETE_QUESTION);

  const [
    reorderMultipleChoiceQuestionOptionMutation,
    { loading: isReorderingOptions, error: reorderOptionsError },
  ] = useMutation<
    ReorderMultipleChoiceQuestionOption,
    ReorderMultipleChoiceQuestionOptionVariables
  >(REORDER_OPTIONS_MUTATION);

  const title = watch("title", question.title);
  const body = watch("body", question.body?.content || "");

  const onSubmitQuestion: SubmitHandler<
    UpdateMultipleChoiceQuestionForm
  > = () => {
    void updateDetailsMutation({
      variables: {
        input: {
          multipleChoiceId,
          multipleChoiceQuestionId: question.id,
          title: { updatedValue: title },
          body: {
            updatedValue: body === null ? null : markdownInput(body),
          },
        },
      },
    });
  };

  useDebounce(title, question.title, 500, handleSubmit(onSubmitQuestion));
  useDebounce(
    body,
    question.body?.content || "",
    500,
    handleSubmit(onSubmitQuestion),
  );

  const moveQuestionOptionElement = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setQuestionOptionElements((prevElements) =>
        update(prevElements, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevElements[dragIndex]],
          ],
        }),
      );
    },
    [],
  );

  const saveQuestionOptionsOrder = React.useCallback(
    async (dragIndex: number, dropIndex: number, id: string) => {
      const moveToAfterElement =
        dropIndex === 0
          ? null
          : (questionOptionElements[dropIndex - 1] ?? null);
      await reorderMultipleChoiceQuestionOptionMutation({
        variables: {
          input: {
            multipleChoiceId: multipleChoiceId,
            questionId: question.id,
            optionId: id,
            moveToAfterOptionId: moveToAfterElement?.id,
          },
        },
      });

      refetch();
    },
    [questionOptionElements],
  );

  const { showConfirm, InlineConfirm } = useConfirmDialog();

  const onDeleteIconClick = () => {
    showConfirm({
      text: "Er du sikker på at du vil slette spørsmålet?",
      okButtonText: "Ja, slett",
      okButtonAction: () => deleteQuestion(),
      cancelButtonText: "Avbryt",
    });
  };

  const deleteQuestion = React.useCallback(async () => {
    await deleteQuestionMutation({
      variables: {
        input: {
          multipleChoiceId: multipleChoiceId,
          questionId: question.id,
        },
      },
    });

    refetch();
  }, [questionOptionElements]);

  const numberOfChecked = question.options?.filter(
    (option) => option.correct,
  ).length;

  const [ref, isDragging] = useDragAndDropElement(
    question.id,
    index,
    DnDItemType.MULTIPLE_CHOICE_QUESTION,
    moveElement,
    saveOrder,
  );

  return (
    <ListItem
      passRef={ref}
      style={{ opacity: isDragging ? 0 : 1 }}
      className={questionStyles.container}
      onDragStart={preventDragOnTextInput}
    >
      <FormProvider {...methods}>
        <form>
          {/* TODO: Add button for deleting question */}
          <div
            className={classNames(
              questionStyles.header,
              isExpanded ? questionStyles.headerExpanded : null,
            )}
          >
            <InputField
              id={question.id}
              placeholder={
                type == MultipleChoiceType.STANDARD
                  ? "Skriv inn spørsmålet her.."
                  : "Skriv inn navn på grupperingskategori.."
              }
              kind="medium"
              aria-label={`Spørsmål: ${question.title}`}
              register={register("title", {
                required: "Vennligst legg til et spørsmål",
                minLength: 1,
              })}
            />
            <FieldError name="title" />

            <MutationLoadingOrError
              loading={isUpdatingDetails}
              error={updateDetailsError}
            />
            <MutationLoadingOrError
              loading={isReorderingOptions}
              error={reorderOptionsError}
            />
            <MutationLoadingOrError
              loading={isDeletingQuestion}
              error={deleteQuestionError}
            />

            {InlineConfirm ?? (
              <>
                <img
                  src={barsicon}
                  alt="icon"
                  className={questionStyles.icon}
                />

                <TextButton
                  onClick={setIsExpanded}
                  kind="secondary"
                  textSize="small"
                >
                  {isExpanded ? "Lukk" : "Utvid"}
                </TextButton>

                <IconButton
                  icon={deleteIcon}
                  altText={`Slett spørsmålet som heter ${question.title}`}
                  onClick={onDeleteIconClick}
                />
              </>
            )}
          </div>
          {isExpanded && (
            <section className={questionStyles.inner}>
              <MarkdownField<UpdateMultipleChoiceQuestionForm>
                name="body"
                title="Tilleggsinformasjon til spørsmålet"
              />
              <ListContainer noMargin>
                {questionOptionElements.map((option, index) => (
                  <QuestionListOptionItem
                    key={option.id}
                    option={option}
                    questionId={question.id}
                    multipleChoiceId={multipleChoiceId}
                    numberOfChecked={numberOfChecked ?? 0}
                    index={index}
                    moveElement={moveQuestionOptionElement}
                    saveOrder={saveQuestionOptionsOrder}
                    type={type}
                    refetch={refetch}
                  />
                ))}
              </ListContainer>
            </section>
          )}
        </form>
      </FormProvider>
      {isExpanded && (
        <div className={questionStyles.inner}>
          <CreateQuestionOptionForm
            questionId={question.id}
            multipleChoiceId={multipleChoiceId}
            type={type}
            refetch={refetch}
          />
        </div>
      )}
    </ListItem>
  );
};
